import {AfriexAdminPermission} from '@afriex/core/domain/enums';

import {PermissionsProvider} from '@/components/common/PermissionsProvider';
import SpiningLoader from '@/components/common/SpinningLoader';
import type {BusinessFee} from '@/types';
import {formatValues} from '@/utils/dashboard';
import * as formatDate from '@/utils/date';

import FeeUpdateButton from './FeeUpdateButton';

type FeeSummaryProp = {
  item: BusinessFee;
  isLoading?: boolean;
  isDetail?: boolean;
  /** Called after the fee is updated so the list can refresh in place. */
  onFeeSaved?: () => void | Promise<void>;
  /** When rendered inside an enterprise profile, scopes fee updates to it. */
  businessId?: string;
  businessName?: string;
};

const FeeSummary = ({
  item,
  isLoading,
  onFeeSaved,
  businessId,
  businessName,
}: FeeSummaryProp) => {
  return isLoading ? (
    <SpiningLoader />
  ) : (
    <div className="bg-white dark:bg-transparent rounded-lg w-full px-5 sm:px-5 py-5 sm:py-5">
      <div className="flex items-center gap-4 mb-3">
        <h2 className="font-semibold text-xl mr-3">{item.name}</h2>
      </div>

      <div>
        <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 dark:bg-gray-900 justify-between">
          <p className="text-slate-500 text-sm dark:text-slate-100">Fee ID</p>
          <p className="text-sm font-medium text-gray-700 w-1/2 dark:text-gray-100">
            {item?.id}
          </p>
        </div>

        <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 dark:bg-gray-900 justify-between">
          <p className="text-slate-500 text-sm dark:text-slate-100">Name</p>
          <p className="text-sm font-medium text-gray-700 w-1/2 dark:text-gray-100">
            {item?.name}
          </p>
        </div>

        <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 dark:bg-gray-900 justify-between">
          <p className="text-slate-500 text-sm dark:text-slate-100">
            Fee - Fixed Amount
          </p>
          <p className="text-sm font-medium text-gray-700 w-1/2 dark:text-gray-100">
            {item?.amount?.valueFixed || 0} {item?.amount?.currencyCode}
          </p>
        </div>

        <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 dark:bg-gray-900 justify-between">
          <p className="text-slate-500 text-sm dark:text-slate-100">
            Fee - Percentage
          </p>
          <p className="text-sm font-medium text-gray-700 w-1/2 dark:text-gray-100">
            {item?.amount?.valuePercent || 0}%
          </p>
        </div>

        <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 dark:bg-gray-900 justify-between">
          <p className="text-slate-500 text-sm dark:text-slate-100">
            Maximum Allowed Fee
          </p>
          <p className="text-sm font-medium text-gray-700 w-1/2 dark:text-gray-100">
            {formatValues(Number(item?.amount?.valueMax || 0), 'money')}{' '}
            {item?.amount?.currencyCode}
          </p>
        </div>

        <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 dark:bg-gray-900 justify-between">
          <p className="text-slate-500 text-sm dark:text-slate-100">
            Fee Application Strategy
          </p>
          <p className="text-sm font-medium text-gray-700 w-1/2 dark:text-gray-100">
            {item?.action || '-'}
          </p>
        </div>

        <div className="mb-2 py-2 px-3 flex items-start gap-3 bg-gray-100 dark:bg-gray-900 justify-between">
          <p className="text-slate-500 text-sm dark:text-slate-100 shrink-0 whitespace-nowrap">
            Filters Applied
          </p>
          <p className="text-sm font-medium text-gray-700 w-1/2 dark:text-gray-100 min-w-0 break-words whitespace-normal text-wrap">
            {item.tags?.join(', ') || '-'}
          </p>
        </div>

        <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 dark:bg-gray-900 justify-between">
          <p className="text-slate-500 text-sm dark:text-slate-100">
            Fee Status
          </p>
          <p className="text-sm font-medium text-gray-700 w-1/2 dark:text-gray-100">
            {!item?.meta?.flags?.isDeactivated ? (
              <span className="text-green-500">Active</span>
            ) : (
              <span className="text-red-500">Inactive</span>
            )}
          </p>
        </div>

        <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 dark:bg-gray-900 justify-between">
          <p className="text-slate-500 text-sm dark:text-slate-100">
            Date Created
          </p>
          <p className="text-sm font-medium text-gray-700 w-1/2 dark:text-gray-100">
            {formatDate.DDMonYYYY(item?.createdAt?.toString() || '')}
          </p>
        </div>

        <div className="mb-2 py-2 px-3 flex items-start gap-3 bg-gray-100 dark:bg-gray-900 justify-between">
          <p className="text-slate-500 text-sm dark:text-slate-100 shrink-0 whitespace-nowrap">
            Last Activity
          </p>
          <p className="text-sm font-medium text-gray-700 w-1/2 dark:text-gray-100 min-w-0 break-words whitespace-normal text-wrap">
            {item?.meta?.logs?.[item?.meta?.logs.length - 1]}
          </p>
        </div>
      </div>

      <div className="text-sm text-slate-500 w-full pt-5 sm:pt-5">
        <div className="flex justify-end gap-4">
          <PermissionsProvider
            permission={AfriexAdminPermission.BusinessFeeManage}
          >
            <FeeUpdateButton
              initialData={item}
              businessId={businessId}
              businessName={businessName}
              onSuccess={onFeeSaved}
            />
          </PermissionsProvider>
        </div>
      </div>
    </div>
  );
};

export default FeeSummary;
